import React, { useState } from 'react';
import {
  useGetClassTermsQuery,
  useCreateTermMutation,
  useUpdateTermMutation,
  useDeleteTermMutation,
} from '../../store/api/localApi';
import ConfirmDialog from '../../components/ConfirmDialog';

/**
 * Term management for a class (§3). Admins list the class's terms and add, edit
 * (move dates), or delete them. The backend guards the dangerous edits:
 *   - a shrink that would orphan an enrolled child 409s with the child list;
 *   - a delete of a term with recorded sessions/absences 409s.
 * Both are surfaced here as a readable blocker list rather than a raw error.
 */
interface Term {
  termId: string;
  name: string;
  startDate: string;
  endDate: string;
}

const blankDraft = { termId: '', name: '', startDate: '', endDate: '' };

const TermManagementPanel: React.FC<{ classId: string }> = ({ classId }) => {
  const { data: terms } = useGetClassTermsQuery(classId);
  const [createTerm] = useCreateTermMutation();
  const [updateTerm] = useUpdateTermMutation();
  const [deleteTerm] = useDeleteTermMutation();

  const [draft, setDraft] = useState<typeof blankDraft>(blankDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [blockers, setBlockers] = useState<string[]>([]);
  // The term the user has asked to delete, held while the confirm dialog is open.
  const [pendingDelete, setPendingDelete] = useState<Term | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const resetForm = () => {
    setDraft(blankDraft);
    setEditingId(null);
    setError('');
    setBlockers([]);
  };

  const startEdit = (t: Term) => {
    setEditingId(t.termId);
    setDraft({ termId: t.termId, name: t.name, startDate: t.startDate, endDate: t.endDate });
    setError('');
    setBlockers([]);
  };

  /** Turn a 409 body into a readable blocker list where the API supplied one. */
  const surface409 = (err: any): boolean => {
    const data = err?.data;
    if (!data) return false;
    if (Array.isArray(data.affectedChildren)) {
      setBlockers(data.affectedChildren.map((c: { studentName: string }) => c.studentName));
      setError(data.message || 'Some enrolled children fall outside the new dates.');
      return true;
    }
    if (Array.isArray(data.blockingTerms)) {
      setBlockers(data.blockingTerms.map((t: { name: string }) => t.name));
      setError(data.message || 'A term has recorded data.');
      return true;
    }
    if (data.error === 'TERM_HAS_OPERATIONAL_DATA') {
      setError(data.message || 'This term has recorded data and cannot be deleted.');
      return true;
    }
    return false;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBlockers([]);
    if (!draft.name || !draft.startDate || !draft.endDate) {
      setError('Name and both dates are required.');
      return;
    }
    if (draft.endDate < draft.startDate) {
      setError('End date must be on or after the start date.');
      return;
    }
    try {
      if (editingId) {
        await updateTerm({
          classId,
          termId: editingId,
          startDate: draft.startDate,
          endDate: draft.endDate,
        }).unwrap();
      } else {
        await createTerm({
          classId,
          name: draft.name,
          startDate: draft.startDate,
          endDate: draft.endDate,
        }).unwrap();
      }
      resetForm();
    } catch (err: any) {
      if (!surface409(err))
        setError(err?.data?.message || err?.data?.error || 'Failed to save term.');
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const termId = pendingDelete.termId;
    setError('');
    setBlockers([]);
    setIsDeleting(true);
    try {
      await deleteTerm({ classId, termId }).unwrap();
      setPendingDelete(null);
    } catch (err: any) {
      // The backend refuses a term with recorded sessions/absences (409). Close
      // the dialog and surface the blocker where the panel already shows errors.
      setPendingDelete(null);
      if (!surface409(err)) setError(err?.data?.message || 'Failed to delete term.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg" data-testid="term-management-panel">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Terms</h3>

        {error && (
          <div
            className="rounded-md bg-red-50 border border-red-200 p-3 mb-4"
            data-testid="term-error"
          >
            <p className="text-sm text-red-700">{error}</p>
            {blockers.length > 0 && (
              <ul
                className="mt-2 list-disc list-inside text-sm text-red-700"
                data-testid="term-blockers"
              >
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <ul className="divide-y divide-gray-200 mb-4" data-testid="term-list">
          {(terms ?? []).map((t) => (
            <li
              key={t.termId}
              data-testid={`term-row-${t.termId}`}
              className="py-3 flex items-center justify-between"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-500">
                  {t.startDate} → {t.endDate}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`term-edit-${t.termId}`}
                  onClick={() => startEdit(t as Term)}
                  className="px-2.5 py-1 border border-gray-300 rounded-md text-xs text-gray-700 bg-white hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  data-testid={`term-delete-${t.termId}`}
                  onClick={() => setPendingDelete(t as Term)}
                  className="px-2.5 py-1 border border-transparent rounded-md text-xs text-white bg-red-600 hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
          {(terms ?? []).length === 0 && (
            <li className="py-3 text-sm text-gray-500" data-testid="term-empty">
              No terms yet.
            </li>
          )}
        </ul>

        <form onSubmit={submit} className="space-y-3 border-t border-gray-100 pt-4">
          <p className="text-xs font-medium text-gray-500 uppercase">
            {editingId ? 'Edit term dates' : 'Add a term'}
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              data-testid="term-name-input"
              value={draft.name}
              disabled={!!editingId}
              onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Autumn term"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Starts</label>
              <input
                type="date"
                data-testid="term-start-input"
                value={draft.startDate}
                onChange={(e) => setDraft((p) => ({ ...p, startDate: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Ends</label>
              <input
                type="date"
                data-testid="term-end-input"
                value={draft.endDate}
                onChange={(e) => setDraft((p) => ({ ...p, endDate: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              data-testid="term-save"
              className="px-3 py-1.5 border border-transparent rounded-md text-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              {editingId ? 'Save dates' : 'Add term'}
            </button>
            {editingId && (
              <button
                type="button"
                data-testid="term-cancel-edit"
                onClick={resetForm}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        title="Delete term"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.name}" (${pendingDelete.startDate} → ${pendingDelete.endDate})? This removes the term and its enrollments. A term with recorded sessions or absences can't be deleted.`
            : ''
        }
        confirmLabel="Delete term"
        confirmTestId="term-delete-confirm"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TermManagementPanel;
